// src/screens/PaymentSettingsScreen.js
// ============================================================================
// PAYMENT SETTINGS - Admin Stripe Control Panel
// 
// Features:
//   - Master on/off switch
//   - Test/Live mode toggle
//   - Deposits and Withdrawals toggles
//   - Min/max limits configuration
//   - Emergency shutdown
//   - Recent deposits overview
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ScrollView,
  ActivityIndicator, Switch, TextInput, Alert, Platform,
  SafeAreaView, RefreshControl
} from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import PurchaseLimitsAdmin from '../components/PurchaseLimitsAdmin';
import DisputesAdmin from '../components/DisputesAdmin';
import MembershipEndAdmin from '../components/MembershipEndAdmin';
import { useAdminAuth } from '../hooks/useAdminAuth';

const functions = getFunctions();

// Helper to format cents
const formatCents = (cents) => `$${(cents / 100).toFixed(2)}`;
const parseDollars = (str) => Math.round(parseFloat(str || '0') * 100);

const COLORS = {
  BG: '#0a0a0a',
  CARD: '#111',
  GOLD: '#FCD000',
  TEXT: '#fff',
  DIM: '#888',
  BORDER: '#222',
  SUCCESS: '#4CAF50',
  DANGER: '#ff4444',
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function PaymentSettingsScreen({ navigation }) {
  const { user, isAdmin, isLoading: authLoading } = useAdminAuth();
  
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);

  // Latest reconciliation snapshot — powers the Turbo Pay float-safety
  // indicator below the toggle. Soft-loaded: if it fails (e.g., no snapshot
  // exists yet because the daily 3 AM UTC run hasn't fired and no admin has
  // hit Run Now), the indicator falls back to "unknown" rather than blocking
  // the rest of the page.
  const [reconciliation, setReconciliation] = useState(null);

  // Active payment-risk holds (chargeback suspensions). null = still loading.
  const [riskHolds, setRiskHolds] = useState(null);

  // Manual reconciliation refresh (float indicator "Refresh snapshot" button).
  const [snapshotRefreshing, setSnapshotRefreshing] = useState(false);

  // Creator Comp card: config (null = loading) + on-demand distribution preview.
  const [compConfig, setCompConfig] = useState(null);
  const [compPreview, setCompPreview] = useState(null);
  const [compPreviewLoading, setCompPreviewLoading] = useState(false);

  // Edit states
  const [depositMin, setDepositMin] = useState('');
  const [depositMax, setDepositMax] = useState('');
  const [withdrawMin, setWithdrawMin] = useState('');
  const [withdrawMax, setWithdrawMax] = useState('');
  const [maintenanceMsg, setMaintenanceMsg] = useState('');

  // ============================================================================
  // LOAD CONFIG
  // ============================================================================
  
  const loadConfig = useCallback(async () => {
    try {
      setError(null);
      
      const getConfig = httpsCallable(functions, 'getStripeAdminConfig');
      const result = await getConfig();
      
      setConfig(result.data);
      
      // Populate edit fields
      setDepositMin((result.data.features.deposits.minCents / 100).toString());
      setDepositMax((result.data.features.deposits.maxCents / 100).toString());
      setWithdrawMin((result.data.features.withdrawals.minCents / 100).toString());
      setWithdrawMax((result.data.features.withdrawals.maxCents / 100).toString());
      setMaintenanceMsg(result.data.maintenance?.message || '');
      
    } catch (err) {
      console.error('Error loading Stripe config:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);
  
  // Soft-loaded like reconciliation — a failure shows an inline message
  // instead of blocking the page.
  const loadRiskHolds = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'adminListPaymentRiskHolds');
      const result = await fn({});
      setRiskHolds(result.data?.holds || []);
    } catch (e) {
      console.warn('[payment-settings] risk holds fetch failed:', e.message);
      setRiskHolds([]);
    }
  }, []);

  // Reconciliation fetch is soft — failure here doesn't block the page.
  const loadReconciliation = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'adminGetReconciliationHistory');
      const result = await fn({ limit: 1 });
      setReconciliation(result.data?.records?.[0] || null);
    } catch (e) {
      console.warn('[payment-settings] reconciliation fetch failed:', e.message);
      setReconciliation(null);
    }
  }, []);

  // "Refresh snapshot" — run reconciliation NOW (same as the dashboard's Run
  // Now) and re-pull the latest record. The nightly 3 AM UTC run continues
  // regardless; this is for on-demand checks (e.g., right after a mode flip).
  const handleRefreshSnapshot = async () => {
    setSnapshotRefreshing(true);
    try {
      const run = httpsCallable(functions, 'adminRunReconciliation');
      await run({});
      await loadReconciliation();
    } catch (err) {
      showError(err.message);
    } finally {
      setSnapshotRefreshing(false);
    }
  };

  const loadCompConfig = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'adminGetCreatorCompConfig');
      const result = await fn();
      setCompConfig(result.data || null);
    } catch (e) {
      console.warn('[payment-settings] comp config fetch failed:', e.message);
      setCompConfig(null);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && isAdmin) {
      loadConfig();
      loadRiskHolds();
      loadReconciliation();
      loadCompConfig();
    } else if (!authLoading && !isAdmin) {
      setLoading(false);
    }
  }, [authLoading, isAdmin, loadConfig, loadRiskHolds, loadReconciliation, loadCompConfig]);

  // ============================================================================
  // CREATOR COMP (enable/test-mode toggles + distribution preview)
  // ============================================================================

  const handleToggleCompEnabled = async (enabled) => {
    setUpdating(true);
    try {
      const fn = httpsCallable(functions, 'adminSetCreatorCompMode');
      await fn({ enabled });
      await loadCompConfig();
    } catch (err) {
      showError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleCompTestMode = async (testMode) => {
    if (!testMode) {
      const confirmed = await confirmAction(
        '💸 Creator Comp → LIVE',
        'Live mode PAYS REAL MONEY on the next run: the 1st-of-month job (and any non-dry manual run) will credit real withdrawable balances.\n\nBefore going live: purge test revenueLedger rows and set platformCostFloorCents.\n\nSwitch comp to LIVE?',
        'GO LIVE'
      );
      if (!confirmed) return;
    }
    setUpdating(true);
    try {
      const fn = httpsCallable(functions, 'adminSetCreatorCompMode');
      await fn({ testMode });
      await loadCompConfig();
    } catch (err) {
      showError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const runCompPreview = async (monthKey) => {
    setCompPreviewLoading(true);
    try {
      const fn = httpsCallable(functions, 'adminRunCreatorComp');
      const result = await fn({ monthKey, dryRun: true });
      setCompPreview(result.data || null);
    } catch (err) {
      showError(err.message);
    } finally {
      setCompPreviewLoading(false);
    }
  };

  const compMonthKey = (offset = 0) => {
    const d = new Date();
    d.setUTCMonth(d.getUTCMonth() + offset);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  };

  const onRefresh = () => {
    setRefreshing(true);
    loadConfig();
    loadRiskHolds();
  };

  // ============================================================================
  // PAYMENT RISK HOLDS (chargeback suspensions)
  // ============================================================================

  const handleClearHold = async (hold) => {
    const who = hold.displayName || hold.email || hold.userId;
    const confirmed = await confirmAction(
      'Clear payment-risk hold?',
      `This restores full access for ${who} (entries, purchases, withdrawals).\n\nReason held: ${hold.reason || 'chargeback'}\nHeld since: ${hold.at ? new Date(hold.at).toLocaleString() : 'unknown'}\n\nOnly clear this after investigating the chargeback.`,
      'Clear Hold'
    );
    if (!confirmed) return;

    setUpdating(true);
    try {
      const clear = httpsCallable(functions, 'adminClearPaymentRiskHold');
      await clear({ userId: hold.userId });
      await loadRiskHolds();
      showMessage(`Hold cleared for ${who}.`);
    } catch (err) {
      showError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  // ============================================================================
  // TOGGLE HANDLERS
  // ============================================================================
  
  const handleToggleMaster = async (enabled) => {
    if (!enabled) {
      const confirmed = await confirmAction(
        'Disable Payments?',
        'This will prevent all deposits and withdrawals. Users will see an error message.'
      );
      if (!confirmed) return;
    }
    
    setUpdating(true);
    try {
      const toggle = httpsCallable(functions, 'toggleStripeEnabled');
      const result = await toggle({ enabled });
      
      setConfig(prev => ({ ...prev, enabled: result.data.enabled }));
      showMessage(result.data.message);
    } catch (err) {
      showError(err.message);
    } finally {
      setUpdating(false);
    }
  };
  
  const handleToggleTestMode = async (testMode) => {
    if (!testMode) {
      // Switching to LIVE - require confirmation
      const confirmed = await confirmAction(
        '⚠️ Switch to LIVE Mode?',
        'WARNING: This will enable REAL charges.\n\nAre you absolutely sure?',
        'GO LIVE'
      );
      if (!confirmed) return;
      
      setUpdating(true);
      try {
        const toggle = httpsCallable(functions, 'toggleStripeTestMode');
        const result = await toggle({ testMode: false, confirmLive: 'GO_LIVE' });
        
        setConfig(prev => ({ ...prev, testMode: result.data.testMode }));
        showMessage(result.data.message);
      } catch (err) {
        showError(err.message);
      } finally {
        setUpdating(false);
      }
    } else {
      // Switching to test - no confirmation needed
      setUpdating(true);
      try {
        const toggle = httpsCallable(functions, 'toggleStripeTestMode');
        const result = await toggle({ testMode: true });
        
        setConfig(prev => ({ ...prev, testMode: result.data.testMode }));
        showMessage(result.data.message);
      } catch (err) {
        showError(err.message);
      } finally {
        setUpdating(false);
      }
    }
  };
  
  const handleToggleDeposits = async (enabled) => {
    setUpdating(true);
    try {
      const toggle = httpsCallable(functions, 'toggleDeposits');
      const result = await toggle({ enabled });
      
      setConfig(prev => ({
        ...prev,
        features: {
          ...prev.features,
          deposits: { ...prev.features.deposits, enabled: result.data.depositsEnabled }
        }
      }));
      showMessage(`Deposits ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      showError(err.message);
    } finally {
      setUpdating(false);
    }
  };
  
  const handleToggleWithdrawals = async (enabled) => {
    setUpdating(true);
    try {
      const toggle = httpsCallable(functions, 'toggleWithdrawals');
      const result = await toggle({ enabled });

      setConfig(prev => ({
        ...prev,
        features: {
          ...prev.features,
          withdrawals: { ...prev.features.withdrawals, enabled: result.data.withdrawalsEnabled }
        }
      }));
      showMessage(`Withdrawals ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      showError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleSubscriptions = async (enabled) => {
    setUpdating(true);
    try {
      const toggle = httpsCallable(functions, 'toggleSubscriptions');
      const result = await toggle({ enabled });

      setConfig(prev => ({
        ...prev,
        features: {
          ...prev.features,
          subscriptions: { ...(prev.features?.subscriptions || {}), enabled: result.data.subscriptionsEnabled }
        }
      }));
      showMessage(`Plans ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      showError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  // Channel memberships (3.5a): this screen can only turn them OFF. The
  // server refuses `enabled: true` (memberships-flip-script); every ON goes
  // through evergreen-3.5a-flip.js --apply and its refusal matrix.
  const handleMembershipsOff = async () => {
    const confirmed = await confirmAction(
      'Turn off channel memberships?',
      "New member checkouts, Join buttons and new members-only modes stop. Members-only chat opens, and members can still chat; each channel's members-only setting is kept and comes back only if memberships are turned on again with the flip script. Renewals, credits, member badges and members-only videos continue.",
      'Turn off'
    );
    if (!confirmed) return;

    setUpdating(true);
    try {
      const update = httpsCallable(functions, 'updateStripeAdminConfig');
      const result = await update({ updates: { features: { memberships: { enabled: false } } } });
      const serverMemberships = result.data?.config?.features?.memberships;

      setConfig(prev => ({
        ...prev,
        features: {
          ...prev.features,
          memberships: {
            ...(prev.features?.memberships || {}),
            ...(serverMemberships || {}),
            enabled: serverMemberships?.enabled === true,
          }
        }
      }));
      showMessage('Channel memberships turned off');
    } catch (err) {
      showError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  // Team memberships (3.5b): OFF-only too. The server takes only
  // features.memberships.team.enabled === false here and refuses any other
  // value (memberships-flip-script); every ON goes through
  // evergreen-3.5a-flip.js --team and its refusal matrix. The channel keys are
  // never sent, so the channel flag is untouched.
  const handleTeamMembershipsOff = async () => {
    const confirmed = await confirmAction(
      'Turn off team memberships?',
      'New team member checkouts, Join buttons and new members-only squad chat modes stop. Members-only squad chat opens. Renewals, splits, badges and the activity record continue. Team memberships come back only with the flip script.',
      'Turn off'
    );
    if (!confirmed) return;

    setUpdating(true);
    try {
      const update = httpsCallable(functions, 'updateStripeAdminConfig');
      const result = await update({ updates: { features: { memberships: { team: { enabled: false } } } } });
      const serverTeam = result.data?.config?.features?.memberships?.team;

      setConfig(prev => ({
        ...prev,
        features: {
          ...prev.features,
          memberships: {
            ...(prev.features?.memberships || {}),
            team: {
              ...(prev.features?.memberships?.team || {}),
              ...(serverTeam || {}),
              enabled: serverTeam?.enabled === true,
            }
          }
        }
      }));
      showMessage('Team memberships turned off');
    } catch (err) {
      showError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleTurboPay = async (enabled) => {
    setUpdating(true);
    try {
      const toggle = httpsCallable(functions, 'toggleTurboPay');
      const result = await toggle({ enabled });

      setConfig(prev => ({
        ...prev,
        features: {
          ...prev.features,
          turboPay: { ...(prev.features?.turboPay || {}), enabled: result.data.turboPayEnabled }
        }
      }));
      showMessage(result.data.message || `Turbo Pay ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      showError(err.message);
    } finally {
      setUpdating(false);
    }
  };
  
  const handleToggleMaintenance = async (active) => {
    setUpdating(true);
    try {
      const setMaint = httpsCallable(functions, 'setStripeMaintenance');
      const result = await setMaint({ 
        active, 
        message: maintenanceMsg || 'Payment processing is temporarily unavailable.' 
      });
      
      setConfig(prev => ({ ...prev, maintenance: result.data.maintenance }));
      showMessage(`Maintenance mode ${active ? 'enabled' : 'disabled'}`);
    } catch (err) {
      showError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  // ============================================================================
  // LIMITS HANDLERS
  // ============================================================================
  
  const handleUpdateDepositLimits = async () => {
    setUpdating(true);
    try {
      const update = httpsCallable(functions, 'updatePaymentLimits');
      await update({ 
        feature: 'deposits',
        minCents: parseDollars(depositMin),
        maxCents: parseDollars(depositMax),
      });
      
      await loadConfig();
      showMessage('Deposit limits updated');
    } catch (err) {
      showError(err.message);
    } finally {
      setUpdating(false);
    }
  };
  
  const handleUpdateWithdrawLimits = async () => {
    setUpdating(true);
    try {
      const update = httpsCallable(functions, 'updatePaymentLimits');
      await update({ 
        feature: 'withdrawals',
        minCents: parseDollars(withdrawMin),
        maxCents: parseDollars(withdrawMax),
      });
      
      await loadConfig();
      showMessage('Withdrawal limits updated');
    } catch (err) {
      showError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  // ============================================================================
  // EMERGENCY SHUTDOWN
  // ============================================================================
  
  const handleEmergencyShutdown = async () => {
    const reason = await promptInput(
      '🚨 Emergency Shutdown',
      'Enter the reason for emergency shutdown (required):',
      'Suspicious activity detected'
    );
    
    if (!reason || reason.length < 10) {
      showError('Reason must be at least 10 characters');
      return;
    }
    
    const confirmed = await confirmAction(
      '🚨 CONFIRM EMERGENCY SHUTDOWN',
      `This will IMMEDIATELY disable all payment processing.\n\nReason: ${reason}\n\nAre you sure?`,
      'SHUTDOWN'
    );
    
    if (!confirmed) return;
    
    setUpdating(true);
    try {
      const shutdown = httpsCallable(functions, 'stripeEmergencyShutdown');
      const result = await shutdown({ 
        reason,
        confirm: 'EMERGENCY_SHUTDOWN'
      });
      
      await loadConfig();
      showMessage(result.data.message);
    } catch (err) {
      showError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleRestoreSystem = async () => {
    const confirmed = await confirmAction(
      '✅ Restore Payment System',
      'This will:\n• Re-enable the payment system\n• Turn off maintenance mode\n• Clear the emergency shutdown status\n\nPayments will be active again. Are you sure?',
      'RESTORE'
    );
    
    if (!confirmed) return;
    
    setUpdating(true);
    try {
      // First, turn off maintenance mode
      const setMaint = httpsCallable(functions, 'setStripeMaintenance');
      await setMaint({ active: false, message: '' });
      
      // Then, re-enable the payment system
      const toggleEnabled = httpsCallable(functions, 'toggleStripeEnabled');
      await toggleEnabled({ enabled: true });
      
      // Reload config to get fresh state
      await loadConfig();
      showMessage('✅ Payment system restored successfully!');
    } catch (err) {
      showError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  // ============================================================================
  // HELPERS
  // ============================================================================
  
  const confirmAction = (title, message, confirmText = 'Confirm') => {
    if (Platform.OS === 'web') {
      return Promise.resolve(window.confirm(`${title}\n\n${message}`));
    }
    return new Promise((resolve) => {
      Alert.alert(title, message, [
        { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
        { text: confirmText, style: 'destructive', onPress: () => resolve(true) },
      ]);
    });
  };
  
  const promptInput = (title, message, defaultValue = '') => {
    if (Platform.OS === 'web') {
      return Promise.resolve(window.prompt(message, defaultValue));
    }
    return new Promise((resolve) => {
      Alert.prompt(title, message, resolve, 'plain-text', defaultValue);
    });
  };
  
  const showMessage = (msg) => {
    if (Platform.OS === 'web') {
      alert(msg);
    } else {
      Alert.alert('Success', msg);
    }
  };
  
  const showError = (msg) => {
    if (Platform.OS === 'web') {
      alert('Error: ' + msg);
    } else {
      Alert.alert('Error', msg);
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  // Auth loading
  if (authLoading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.GOLD} />
        </View>
      </SafeAreaView>
    );
  }

  // Not admin
  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.loadingContainer}>
          <Text style={styles.errorText}>Admin access required</Text>
          <TouchableOpacity style={styles.backBtn} onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}>
            <Text style={styles.backBtnText}>← Go Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }
  
  // Config loading
  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <Header navigation={navigation} />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.GOLD} />
          <Text style={styles.loadingText}>Loading Stripe Config...</Text>
        </View>
        <Footer navigation={navigation} />
      </SafeAreaView>
    );
  }
  
  // Error state
  if (error && !config) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <Header navigation={navigation} />
        <View style={styles.loadingContainer}>
          <Text style={styles.errorText}>Error: {error}</Text>
          <TouchableOpacity style={styles.retryBtn} onPress={loadConfig}>
            <Text style={styles.retryBtnText}>Retry</Text>
          </TouchableOpacity>
        </View>
        <Footer navigation={navigation} />
      </SafeAreaView>
    );
  }
  
  const isLive = !config?.testMode;
  const isEnabled = config?.enabled;

  return (
    <SafeAreaView style={styles.safeArea}>
      <Header navigation={navigation} />
      <ScrollView 
        style={styles.container} 
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={COLORS.GOLD} />
        }
      >
        {updating && (
          <View style={styles.updatingOverlay}>
            <ActivityIndicator color={COLORS.GOLD} />
          </View>
        )}
        
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Payment Settings</Text>
          <Text style={styles.subtitle}>Stripe Configuration & Controls</Text>
        </View>
        
        {/* Status Banner */}
        <View style={[
          styles.statusBanner,
          isEnabled 
            ? (isLive ? styles.statusLive : styles.statusTest)
            : styles.statusDisabled
        ]}>
          <Text style={styles.statusIcon}>
            {isEnabled ? (isLive ? '🔴' : '🧪') : '⏸️'}
          </Text>
          <View style={styles.statusTextContainer}>
            <Text style={styles.statusTitle}>
              {isEnabled 
                ? (isLive ? 'LIVE MODE' : 'TEST MODE')
                : 'PAYMENTS DISABLED'
              }
            </Text>
            <Text style={styles.statusSubtitle}>
              {isEnabled
                ? (isLive ? 'Real charges are active!' : 'Using test API keys')
                : 'All payment processing is off'
              }
            </Text>
          </View>
        </View>
        
        {/* API Key Status */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>API Keys</Text>
          <View style={styles.keyStatus}>
            <Text style={styles.keyLabel}>Mode: </Text>
            <Text style={[styles.keyValue, isLive ? styles.keyLive : styles.keyTest]}>
              {config?.keyInfo?.keyPrefix || 'Unknown'}
            </Text>
          </View>
          <View style={styles.keyChecks}>
            <KeyCheck label="Secret Key" configured={config?.keyInfo?.secretKeyConfigured} />
            <KeyCheck label="Webhook Secret" configured={config?.keyInfo?.webhookSecretConfigured} />
          </View>
          <Text style={styles.keyNote}>
            Publishable keys are configured in your frontend code, not here.
          </Text>
        </View>
        
        {/* Master Controls */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Master Controls</Text>
          
          <ToggleRow
            label="Payments Enabled"
            description="Master switch for all payment processing"
            value={config?.enabled}
            onToggle={handleToggleMaster}
            disabled={updating}
          />
          
          <ToggleRow
            label="Test Mode"
            description={config?.testMode ? 'Using test API keys' : '⚠️ LIVE - Real charges!'}
            value={config?.testMode}
            onToggle={handleToggleTestMode}
            disabled={updating || !config?.enabled}
            danger={!config?.testMode}
          />
          
          <ToggleRow
            label="Maintenance Mode"
            description="Show maintenance message to users"
            value={config?.maintenance?.active}
            onToggle={handleToggleMaintenance}
            disabled={updating}
          />
          
          {config?.maintenance?.active && (
            <View style={styles.maintenanceMsgBox}>
              <TextInput
                style={styles.maintenanceMsgInput}
                value={maintenanceMsg}
                onChangeText={setMaintenanceMsg}
                placeholder="Maintenance message..."
                placeholderTextColor="#666"
                multiline
              />
            </View>
          )}
        </View>
        
        {/* Feature Controls */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Features</Text>
          
          <ToggleRow
            label="Deposits"
            description={`${formatCents(config?.features?.deposits?.minCents || 500)} - ${formatCents(config?.features?.deposits?.maxCents || 100000)}`}
            value={config?.features?.deposits?.enabled}
            onToggle={handleToggleDeposits}
            disabled={updating || !config?.enabled}
          />
          
          <ToggleRow
            label="Withdrawals"
            description={`${formatCents(config?.features?.withdrawals?.minCents || 1000)} - ${formatCents(config?.features?.withdrawals?.maxCents || 500000)}`}
            value={config?.features?.withdrawals?.enabled}
            onToggle={handleToggleWithdrawals}
            disabled={updating || !config?.enabled}
          />

          <ToggleRow
            label="Plans"
            description="Paid plans (Rookie, Pro, Elite). Required for plan checkout."
            value={config?.features?.subscriptions?.enabled}
            onToggle={handleToggleSubscriptions}
            disabled={updating || !config?.enabled}
          />

          {/* Channel memberships: OFF-only here (the server refuses true). */}
          <View style={styles.membershipsRow}>
            <Text style={styles.toggleLabel}>Channel memberships</Text>
            {config?.features?.memberships?.enabled === true ? (
              <TouchableOpacity
                style={[styles.membershipsOffBtn, updating && styles.membershipsOffBtnDisabled]}
                onPress={handleMembershipsOff}
                disabled={updating}
                accessibilityRole="button"
                accessibilityState={{ disabled: updating }}
              >
                <Text style={styles.membershipsOffBtnText}>Turn off channel memberships</Text>
              </TouchableOpacity>
            ) : (
              <Text style={styles.toggleDesc}>Off. Channel memberships are turned on with the flip script.</Text>
            )}
          </View>

          {/* Team memberships (3.5b): OFF-only here too (the server refuses
              true for the team key). */}
          <View style={styles.membershipsRow}>
            <Text style={styles.toggleLabel}>Team memberships</Text>
            {config?.features?.memberships?.team?.enabled === true ? (
              <TouchableOpacity
                style={[styles.membershipsOffBtn, updating && styles.membershipsOffBtnDisabled]}
                onPress={handleTeamMembershipsOff}
                disabled={updating}
                accessibilityRole="button"
                accessibilityState={{ disabled: updating }}
              >
                <Text style={styles.membershipsOffBtnText}>Turn off team memberships</Text>
              </TouchableOpacity>
            ) : (
              <Text style={styles.toggleDesc}>Off. Team memberships are turned on with the flip script.</Text>
            )}
          </View>
        </View>

        {/* Turbo Pay (Instant Withdrawals) */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Turbo Pay (Instant Withdrawals)</Text>

          <ToggleRow
            label="Turbo Pay"
            description="Stripe instant payouts: $10 flat ≤$500, 2% above. CritCash absorbs Stripe's 1.5% instant fee per withdrawal — net ~0.5% margin. Float burst risk applies."
            value={config?.features?.turboPay?.enabled}
            onToggle={handleToggleTurboPay}
            disabled={updating || !config?.enabled || !config?.features?.withdrawals?.enabled}
          />

          <FloatSafetyIndicator
            reconciliation={reconciliation}
            currentMode={config?.testMode ? 'test' : 'live'}
            onRefresh={handleRefreshSnapshot}
            refreshing={snapshotRefreshing}
          />
        </View>

        {/* Deposit Limits */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Deposit Limits</Text>
          <View style={styles.limitsRow}>
            <View style={styles.limitInput}>
              <Text style={styles.limitLabel}>Min ($)</Text>
              <TextInput
                style={styles.input}
                value={depositMin}
                onChangeText={setDepositMin}
                keyboardType="decimal-pad"
                placeholder="5.00"
                placeholderTextColor="#666"
              />
            </View>
            <View style={styles.limitInput}>
              <Text style={styles.limitLabel}>Max ($)</Text>
              <TextInput
                style={styles.input}
                value={depositMax}
                onChangeText={setDepositMax}
                keyboardType="decimal-pad"
                placeholder="1000.00"
                placeholderTextColor="#666"
              />
            </View>
            <TouchableOpacity 
              style={styles.updateBtn}
              onPress={handleUpdateDepositLimits}
              disabled={updating}
            >
              <Text style={styles.updateBtnText}>Update</Text>
            </TouchableOpacity>
          </View>
        </View>
        
        {/* Withdrawal Limits */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Withdrawal Limits</Text>
          <View style={styles.limitsRow}>
            <View style={styles.limitInput}>
              <Text style={styles.limitLabel}>Min ($)</Text>
              <TextInput
                style={styles.input}
                value={withdrawMin}
                onChangeText={setWithdrawMin}
                keyboardType="decimal-pad"
                placeholder="10.00"
                placeholderTextColor="#666"
              />
            </View>
            <View style={styles.limitInput}>
              <Text style={styles.limitLabel}>Max ($)</Text>
              <TextInput
                style={styles.input}
                value={withdrawMax}
                onChangeText={setWithdrawMax}
                keyboardType="decimal-pad"
                placeholder="5000.00"
                placeholderTextColor="#666"
              />
            </View>
            <TouchableOpacity 
              style={styles.updateBtn}
              onPress={handleUpdateWithdrawLimits}
              disabled={updating}
            >
              <Text style={styles.updateBtnText}>Update</Text>
            </TouchableOpacity>
          </View>
        </View>
        
        {/* Payment Risk Holds (chargeback suspensions) */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Payment Risk Holds</Text>
          <Text style={styles.keyNote}>
            Accounts suspended automatically after a chargeback. Held users can't enter events,
            make purchases, or withdraw until cleared.
          </Text>
          {riskHolds === null ? (
            <ActivityIndicator color={COLORS.GOLD} style={styles.holdSpinner} />
          ) : riskHolds.length === 0 ? (
            <Text style={styles.holdsEmpty}>No active holds.</Text>
          ) : (
            riskHolds.map((hold) => (
              <View key={hold.id} style={styles.holdRow}>
                <View style={styles.holdInfo}>
                  <Text style={styles.holdName}>
                    {hold.displayName || hold.email || hold.userId}
                  </Text>
                  <Text style={styles.holdMeta}>
                    {hold.reason || 'chargeback'}
                    {hold.amountCents ? ` · $${(hold.amountCents / 100).toFixed(2)}` : ''}
                    {hold.at ? ` · ${new Date(hold.at).toLocaleDateString()}` : ''}
                  </Text>
                  {hold.email && hold.displayName ? (
                    <Text style={styles.holdMeta}>{hold.email}</Text>
                  ) : null}
                </View>
                <TouchableOpacity
                  style={styles.clearHoldBtn}
                  onPress={() => handleClearHold(hold)}
                  disabled={updating}
                >
                  <Text style={styles.clearHoldBtnText}>Clear Hold</Text>
                </TouchableOpacity>
              </View>
            ))
          )}
        </View>

        {/* Purchase limits: the mode and card-check switches, shares in
            review, cards (STEP3_5A_TIERS_DESIGN 3.7; its own callables) */}
        <PurchaseLimitsAdmin />

        {/* Disputes: open disputes and issuer fraud warnings, the evidence
            packet, and the warning-refund, 3D Secure and join-gate settings
            (STEP3_5A_TIERS_DESIGN 3.13.5, 5.14; R6; its own callables) */}
        <DisputesAdmin />

        {/* End a membership: adminEndMembership after a confirm, with the
            refund and the ending notice; the dark check's last step and
            support ends (FLIP_GAPS_DESIGN 2; its own callable) */}
        <MembershipEndAdmin />

        {/* Creator Comp */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Creator Comp</Text>
          <Text style={styles.keyNote}>
            Monthly revenue-share run (1st, 2 AM UTC). Test mode computes the full
            distribution from real signals and revenue but pays nobody — flip live
            only after purging test revenue rows and setting the cost floor.
          </Text>
          {compConfig === null ? (
            <ActivityIndicator color={COLORS.GOLD} style={styles.holdSpinner} />
          ) : (
            <>
              <View style={[
                styles.compModeBadge,
                compConfig.testMode ? styles.compModeBadgeTest : styles.compModeBadgeLive,
              ]}>
                <Text style={styles.compModeBadgeText}>
                  {compConfig.enabled
                    ? (compConfig.testMode ? '🧪 ENABLED — TEST (previews only, nobody paid)' : '🔴 ENABLED — LIVE (pays real money on the 1st)')
                    : '⏸️ DISABLED'}
                </Text>
              </View>

              <ToggleRow
                label="Comp Enabled"
                description="Master switch for the monthly comp run"
                value={compConfig.enabled}
                onToggle={handleToggleCompEnabled}
                disabled={updating}
              />
              <ToggleRow
                label="Test Mode"
                description={compConfig.testMode ? 'Runs write previews only — no payouts' : '⚠️ LIVE — runs credit real withdrawable balances!'}
                value={compConfig.testMode}
                onToggle={handleToggleCompTestMode}
                disabled={updating}
              />

              <Text style={styles.keyNote}>
                Allocation: {compConfig.allocationMode} · floor ${((compConfig.platformCostFloorCents || 0) / 100).toFixed(2)}/mo
                {compConfig.allocationMode === 'auto' && !compConfig.platformCostFloorCents ? ' ⚠️ SET THE FLOOR (settings/creatorComp)' : ''}
                {' '}· margin {compConfig.targetMarginPercent}% · surplus share {compConfig.surplusSharePercent}% · cap {compConfig.maxAllocationPercent}%
              </Text>

              <View style={styles.compPreviewBtnRow}>
                <TouchableOpacity
                  style={styles.compPreviewBtn}
                  onPress={() => runCompPreview(compMonthKey(0))}
                  disabled={compPreviewLoading}
                >
                  <Text style={styles.compPreviewBtnText}>Preview This Month</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.compPreviewBtn}
                  onPress={() => runCompPreview(compMonthKey(-1))}
                  disabled={compPreviewLoading}
                >
                  <Text style={styles.compPreviewBtnText}>Preview Last Month</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.compPreviewBtn}
                  onPress={() => navigation.navigate('AdminCompOverview')}
                >
                  <Text style={styles.compPreviewBtnText}>📊 Comp Overview</Text>
                </TouchableOpacity>
              </View>

              {compPreviewLoading && <ActivityIndicator color={COLORS.GOLD} style={styles.holdSpinner} />}
              {compPreview && !compPreviewLoading && (
                <View style={styles.compPreviewBox}>
                  <Text style={styles.compPreviewTitle}>
                    {compPreview.month} {compPreview.skipped ? `— skipped (${compPreview.skipped})` : '— dry run'}
                  </Text>
                  {!compPreview.skipped && (
                    <>
                      <Text style={styles.compPreviewLine}>Revenue (comp-funding): ${((compPreview.generalRevenueCents || 0) / 100).toFixed(2)} + Rumble-events ${((compPreview.floodRevenueCents || 0) / 100).toFixed(2)}</Text>
                      <Text style={styles.compPreviewLine}>Pool: ${((compPreview.poolCents || 0) / 100).toFixed(2)} ({compPreview.effectiveAllocationPercent ?? compPreview.allocationPercent ?? 0}% effective, {compPreview.allocationMode})</Text>
                      <Text style={styles.compPreviewLine}>Would pay {compPreview.recipientCount || 0} creator(s) a total of ${((compPreview.distributedCents || 0) / 100).toFixed(2)}</Text>
                    </>
                  )}
                </View>
              )}
            </>
          )}
        </View>

        {/* Emergency Shutdown */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Emergency Controls</Text>
          
          {/* Show restore button if system is disabled or in maintenance */}
          {(!config?.enabled || config?.maintenance?.active) ? (
            <View style={styles.emergencyActive}>
              <Text style={styles.emergencyActiveTitle}>
                ⚠️ {config?.maintenance?.active ? 'System in Maintenance Mode' : 'Payments Disabled'}
              </Text>
              {config?.maintenance?.message && (
                <Text style={styles.emergencyActiveReason}>
                  Message: {config.maintenance.message}
                </Text>
              )}
              {config?.emergencyShutdown?.reason && (
                <Text style={styles.emergencyActiveReason}>
                  Shutdown Reason: {config.emergencyShutdown.reason}
                </Text>
              )}
              
              <TouchableOpacity 
                style={styles.restoreBtn}
                onPress={handleRestoreSystem}
                disabled={updating}
              >
                <Text style={styles.restoreBtnText}>
                  {updating ? 'Restoring...' : '✅ RESTORE PAYMENT SYSTEM'}
                </Text>
              </TouchableOpacity>
            </View>
          ) : (
            <>
              <TouchableOpacity 
                style={styles.emergencyBtn}
                onPress={handleEmergencyShutdown}
                disabled={updating}
              >
                <Text style={styles.emergencyBtnText}>🚨 EMERGENCY SHUTDOWN</Text>
              </TouchableOpacity>
              <Text style={styles.emergencyNote}>
                Immediately disables all payment processing. Use for fraud, security issues, or critical bugs.
              </Text>
            </>
          )}
        </View>
        
        {/* Last Updated */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            Last updated: {config?.updatedAt ? new Date(config.updatedAt.toDate?.() || config.updatedAt).toLocaleString() : 'Never'}
          </Text>
          <Text style={styles.footerText}>
            By: {config?.updatedBy || 'System'}
          </Text>
        </View>
        
        {/* Back Button */}
        <TouchableOpacity style={styles.backBtn} onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}>
          <Text style={styles.backBtnText}>← Back to Dashboard</Text>
        </TouchableOpacity>
        
      </ScrollView>
      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const ToggleRow = ({ label, description, value, onToggle, disabled, danger }) => (
  <View style={styles.toggleRow}>
    <View style={styles.toggleInfo}>
      <Text style={styles.toggleLabel}>{label}</Text>
      <Text style={[styles.toggleDesc, danger && styles.toggleDescDanger]}>
        {description}
      </Text>
    </View>
    <Switch
      value={value}
      onValueChange={onToggle}
      disabled={disabled}
      trackColor={{ false: '#333', true: danger ? '#ff4444' : '#4CAF50' }}
      thumbColor={value ? '#fff' : '#888'}
    />
  </View>
);

const KeyCheck = ({ label, configured }) => (
  <View style={styles.keyCheckRow}>
    <Text style={[styles.keyCheckIcon, { color: configured ? COLORS.SUCCESS : COLORS.DANGER }]}>
      {configured ? '✓' : '✗'}
    </Text>
    <Text style={[styles.keyCheckLabel, !configured && styles.keyCheckMissing]}>
      {label}
    </Text>
  </View>
);

// Float-safety indicator for Turbo Pay enablement. Reads the latest
// reconciliation_log snapshot and computes how much surplus Stripe holds
// above the strict custodial floor (Σ user wallets − Σ in-flight withdrawals).
// That surplus represents CritCash's retained revenue + safety buffer; it's
// what would absorb the immediate 1.5% Stripe instant-payout fee on a
// turbo withdrawal before deposits replenish the platform balance.
//
// Thresholds chosen for bootstrap-stage operation:
//   - Floor breach (Stripe < custodial floor): RED — never enable turbo here
//   - $0 ≤ buffer < $500: YELLOW — tight, single small turbo is fine but
//                                  burst risk is real
//   - buffer ≥ $500: GREEN — safe for typical bootstrap volume
// Tune the $500 threshold upward as expected concurrent turbo volume grows
// (rule of thumb: buffer should comfortably cover 3× your max concurrent
// turbo withdrawal × 1.5% instant fee — but at solo-founder volume that
// number is so small the $500 floor dominates).
const FloatSafetyIndicator = ({ reconciliation, currentMode, onRefresh, refreshing }) => {
  const refreshBtn = onRefresh ? (
    <TouchableOpacity
      style={styles.floatRefreshBtn}
      onPress={onRefresh}
      disabled={refreshing}
    >
      <Text style={styles.floatRefreshBtnText}>
        {refreshing ? 'Refreshing…' : '↻ Refresh snapshot'}
      </Text>
    </TouchableOpacity>
  ) : null;

  if (!reconciliation) {
    return (
      <View style={[styles.floatIndicator, styles.floatIndicatorUnknown]}>
        <Text style={styles.floatIndicatorTitle}>Float status: unknown</Text>
        <Text style={styles.floatIndicatorBody}>
          No reconciliation snapshot loaded. Refresh below (or use the Reconciliation dashboard) to compute the current float buffer before enabling Turbo Pay.
        </Text>
        {refreshBtn}
      </View>
    );
  }

  // Mode guard: a snapshot taken in the OTHER Stripe mode says nothing about
  // the money you're operating on now (test balances aren't real; live
  // balances aren't visible to test keys). Don't present its drift/float as
  // meaningful — ask for a fresh run instead.
  if (currentMode && reconciliation.mode && reconciliation.mode !== currentMode) {
    return (
      <View style={[styles.floatIndicator, styles.floatIndicatorUnknown]}>
        <Text style={styles.floatIndicatorTitle}>
          ⚠️ Snapshot is from {String(reconciliation.mode).toUpperCase()} mode
        </Text>
        <Text style={styles.floatIndicatorBody}>
          The platform is now in {String(currentMode).toUpperCase()} mode, so this snapshot's balances and drift don't reflect real state. Refresh to compute current numbers.
        </Text>
        {refreshBtn}
      </View>
    );
  }

  const summary = reconciliation.summary || {};
  const components = reconciliation.components || {};
  const stripeTotal = components.stripeBalance?.totalCents || 0;
  const userOwed = summary.userOwedAfterPendingCents || 0;
  const bufferCents = stripeTotal - userOwed;
  const floorBreached = !summary.floorOK;

  const dateStr = reconciliation.computedAt
    ? new Date(reconciliation.computedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : 'unknown';

  let toneStyle, title, body;
  if (floorBreached) {
    toneStyle = styles.floatIndicatorDanger;
    title = '🚫 DO NOT ENABLE — Stripe shortfall';
    body = `Stripe is short ${formatCents(summary.stripeShortfallCents || 0)} on user funds right now. Resolve the custodial floor breach before turning Turbo Pay on. Snapshot from ${dateStr}.`;
  } else if (bufferCents < 50000) {
    toneStyle = styles.floatIndicatorWarn;
    title = `⚠️ Tight float — proceed with caution`;
    body = `Stripe holds ${formatCents(bufferCents)} above what you owe users. A single small turbo is safe, but a burst of concurrent turbo requests could push you toward the floor. Snapshot from ${dateStr}.`;
  } else {
    toneStyle = styles.floatIndicatorOk;
    title = `✓ Safe to enable — ${formatCents(bufferCents)} float buffer`;
    body = `Stripe holds ${formatCents(bufferCents)} above the custodial floor (${formatCents(userOwed)} owed to users, ${formatCents(stripeTotal)} actually in Stripe). Comfortable margin for absorbing 1.5% instant-payout fees on turbo withdrawals. Snapshot from ${dateStr}.`;
  }

  return (
    <View style={[styles.floatIndicator, toneStyle]}>
      <Text style={styles.floatIndicatorTitle}>{title}</Text>
      <Text style={styles.floatIndicatorBody}>{body}</Text>
      {refreshBtn}
    </View>
  );
};

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: COLORS.BG,
  },
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
    paddingBottom: 120,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: COLORS.DIM,
    marginTop: 12,
  },
  errorText: {
    color: COLORS.DANGER,
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 16,
  },
  retryBtn: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: COLORS.GOLD,
    borderRadius: 8,
  },
  retryBtnText: {
    color: '#000',
    fontWeight: '700',
  },
  updatingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100,
  },
  
  // Header
  header: {
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: '900',
    color: COLORS.GOLD,
  },
  subtitle: {
    color: COLORS.DIM,
    fontSize: 14,
    marginTop: 4,
  },
  
  // Status Banner
  statusBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    marginBottom: 20,
  },
  statusDisabled: {
    backgroundColor: '#333',
  },
  statusTest: {
    backgroundColor: '#1a3a1a',
    borderWidth: 1,
    borderColor: '#4CAF50',
  },
  statusLive: {
    backgroundColor: '#3a1a1a',
    borderWidth: 1,
    borderColor: '#ff4444',
  },
  statusIcon: {
    fontSize: 32,
    marginRight: 16,
  },
  statusTextContainer: {
    flex: 1,
  },
  statusTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
  },
  statusSubtitle: {
    color: '#aaa',
    fontSize: 14,
    marginTop: 2,
  },
  
  // Sections
  section: {
    backgroundColor: COLORS.CARD,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
  },
  sectionTitle: {
    color: COLORS.GOLD,
    fontSize: 14,
    fontWeight: '700',
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  
  // Key Status
  keyStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  keyLabel: {
    color: COLORS.DIM,
    fontSize: 14,
  },
  keyValue: {
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    fontSize: 14,
  },
  keyTest: {
    color: COLORS.SUCCESS,
  },
  keyLive: {
    color: COLORS.DANGER,
  },
  keyChecks: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  keyCheckRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  keyCheckIcon: {
    marginRight: 6,
    fontSize: 14,
  },
  keyCheckLabel: {
    color: COLORS.SUCCESS,
    fontSize: 13,
  },
  keyCheckMissing: {
    color: COLORS.DANGER,
  },
  keyNote: {
    color: '#555',
    fontSize: 11,
    marginTop: 8,
    fontStyle: 'italic',
  },

  // Float-safety indicator (Turbo Pay section)
  floatIndicator: {
    marginTop: 12,
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
  },
  floatIndicatorOk: {
    backgroundColor: 'rgba(76,175,80,0.10)',
    borderColor: COLORS.SUCCESS,
  },
  floatIndicatorWarn: {
    backgroundColor: 'rgba(245,158,11,0.12)',
    borderColor: '#f59e0b',
  },
  floatIndicatorDanger: {
    backgroundColor: 'rgba(255,68,68,0.12)',
    borderColor: COLORS.DANGER,
  },
  floatIndicatorUnknown: {
    backgroundColor: 'rgba(136,136,136,0.10)',
    borderColor: COLORS.DIM,
  },
  floatIndicatorTitle: {
    color: COLORS.TEXT,
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 4,
  },
  floatIndicatorBody: {
    color: COLORS.DIM,
    fontSize: 12,
    lineHeight: 17,
  },

  // Toggle Rows
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.BORDER,
  },
  toggleInfo: {
    flex: 1,
    marginRight: 16,
  },
  toggleLabel: {
    color: COLORS.TEXT,
    fontSize: 16,
    fontWeight: '600',
  },
  toggleDesc: {
    color: COLORS.DIM,
    fontSize: 13,
    marginTop: 2,
  },
  toggleDescDanger: {
    color: COLORS.DANGER,
  },
  
  // Maintenance Message
  maintenanceMsgBox: {
    marginTop: 12,
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    padding: 12,
  },
  maintenanceMsgInput: {
    color: COLORS.TEXT,
    fontSize: 14,
    minHeight: 60,
  },
  
  // Limits
  limitsRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 12,
  },
  limitInput: {
    flex: 1,
  },
  limitLabel: {
    color: COLORS.DIM,
    fontSize: 12,
    marginBottom: 6,
  },
  input: {
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    padding: 12,
    color: COLORS.TEXT,
    fontSize: 16,
    borderWidth: 1,
    borderColor: '#333',
  },
  updateBtn: {
    backgroundColor: '#333',
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderRadius: 8,
  },
  updateBtnText: {
    color: COLORS.TEXT,
    fontWeight: '600',
  },
  
  // Channel memberships (OFF-only row)
  membershipsRow: {
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.BORDER,
  },
  membershipsOffBtn: {
    alignSelf: 'flex-start',
    backgroundColor: COLORS.DANGER,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    marginTop: 8,
  },
  membershipsOffBtnDisabled: {
    opacity: 0.5,
  },
  membershipsOffBtnText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },

  // Emergency
  emergencyBtn: {
    backgroundColor: COLORS.DANGER,
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  emergencyBtnText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  emergencyNote: {
    color: COLORS.DIM,
    fontSize: 12,
    textAlign: 'center',
    marginTop: 8,
  },
  emergencyActive: {
    marginTop: 16,
    backgroundColor: '#3a1a1a',
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.DANGER,
  },
  emergencyActiveTitle: {
    color: COLORS.DANGER,
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 8,
  },
  emergencyActiveReason: {
    color: COLORS.TEXT,
    fontSize: 14,
    marginBottom: 8,
  },
  restoreBtn: {
    backgroundColor: COLORS.SUCCESS,
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 16,
  },
  restoreBtnText: {
    color: '#000',
    fontSize: 16,
    fontWeight: '700',
  },
  
  // Footer
  footer: {
    alignItems: 'center',
    paddingTop: 20,
    marginBottom: 16,
  },
  footerText: {
    color: '#666',
    fontSize: 12,
  },
  
  // Back Button
  backBtn: {
    padding: 16,
    alignItems: 'center',
  },
  backBtnText: {
    color: COLORS.DIM,
    fontSize: 16,
  },

  // Float indicator refresh
  floatRefreshBtn: {
    alignSelf: 'flex-start',
    marginTop: 10,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: COLORS.GOLD,
  },
  floatRefreshBtnText: {
    color: COLORS.GOLD,
    fontSize: 12,
    fontWeight: '700',
  },

  // Creator Comp
  compModeBadge: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 10,
    marginTop: 12,
    marginBottom: 4,
  },
  compModeBadgeTest: {
    backgroundColor: 'rgba(52, 152, 219, 0.10)',
    borderColor: '#3498DB',
  },
  compModeBadgeLive: {
    backgroundColor: 'rgba(231, 76, 60, 0.12)',
    borderColor: '#E74C3C',
  },
  compModeBadgeText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '700',
  },
  compPreviewBtnRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 12,
  },
  compPreviewBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.GOLD,
    alignItems: 'center',
  },
  compPreviewBtnText: {
    color: COLORS.GOLD,
    fontSize: 13,
    fontWeight: '700',
  },
  compPreviewBox: {
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#2a2a2a',
    backgroundColor: '#141414',
  },
  compPreviewTitle: {
    color: COLORS.GOLD,
    fontSize: 13,
    fontWeight: '800',
    marginBottom: 6,
  },
  compPreviewLine: {
    color: '#ccc',
    fontSize: 12,
    marginTop: 2,
  },

  // Payment Risk Holds
  holdSpinner: {
    marginTop: 12,
  },
  holdsEmpty: {
    color: '#666',
    fontSize: 14,
    marginTop: 12,
    fontStyle: 'italic',
  },
  holdRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 80, 80, 0.35)',
    backgroundColor: 'rgba(255, 80, 80, 0.08)',
  },
  holdInfo: {
    flex: 1,
    marginRight: 12,
  },
  holdName: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '700',
  },
  holdMeta: {
    color: '#aaa',
    fontSize: 12,
    marginTop: 2,
  },
  clearHoldBtn: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    backgroundColor: COLORS.GOLD,
  },
  clearHoldBtnText: {
    color: '#000',
    fontSize: 13,
    fontWeight: '700',
  },
});